<div class="as-built-container">
    <div class="workspace-topbar">
        <div class="topbar-title">
            <div>
                <h1>{{ 'asBuilt' | t }}</h1>
                <p>Draft, review, crop, scale, and publish AC1203 drawings.</p>
            </div>
            <div class="status-group">
                <span class="doc-status">{{ docStatus() }}</span>
                <span class="mode-pill">{{ activeTool() === 'select' ? 'Select' : activeTool() }}</span>
            </div>
        </div>

        <div class="topbar-actions">
            <button class="btn-extract" (click)="goBack()">{{ 'back' | t }}</button>
            @if (currentJobId()) {
                <button class="btn-save" (click)="saveCurrentJob()" [disabled]="isSaving()">
                    {{ isSaving() ? ('loading' | t) : ('save' | t) }}
                </button>
            }
            @if (trainingSampleId()) {
                <button class="btn-save" (click)="submitTrainingExample()" [disabled]="isSubmittingTraining()">
                    {{ isSubmittingTraining() ? 'Submitting...' : 'Submit Example' }}
                </button>
            }
            <button class="btn-undo" (click)="undo()" [disabled]="!canUndo()">Undo</button>
            <button class="btn-undo" (click)="redo()" [disabled]="!canRedo()">Redo</button>
            <button class="btn-delete" (click)="deleteSelected()" [disabled]="!selectedId()">{{ 'delete' | t }}</button>
            <button class="btn-generate" (click)="generatePdf()" [disabled]="isGenerating()">
                    {{ isGenerating() ? ('loading' | t) : ('downloadPdf' | t) }}
            </button>
        </div>
    </div>

    <div class="workspace-layout">
        <aside class="tool-panel">
            <div class="panel-card">
                <div class="panel-heading">
                    <h3>{{ 'tools' | t }}</h3>
                    <p>Pick a mode, then work directly on the sheet.</p>
                </div>

                <div class="tool-stack">
                    <button class="tool-btn" [class.active]="activeTool()==='select'" (click)="setTool('select')">Select / Move</button>
                    <button class="tool-btn ss-tool" [class.active]="activeTool()==='draw-ss'" (click)="setTool('draw-ss')">Sewer Line</button>
                    <button class="tool-btn sw-tool" [class.active]="activeTool()==='draw-sw'" (click)="setTool('draw-sw')">Storm Line</button>
                    <button class="tool-btn cust-tool" [class.active]="activeTool()==='draw-custom'" (click)="setTool('draw-custom')">Custom Line</button>
                    <button class="tool-btn bld-tool" [class.active]="activeTool()==='draw-building'" (click)="setTool('draw-building')">Building Outline</button>
                    <button class="tool-btn msr-tool" [class.active]="activeTool()==='measure'" (click)="setTool('measure')">Measurement</button>
                    <button class="tool-btn dir-tool" [class.active]="activeTool()==='direction'" (click)="setTool('direction')">Direction Line</button>
                    <button class="tool-btn crop-tool" [class.active]="activeTool()==='crop'" (click)="setTool('crop')">Manual Crop</button>
                </div>

                <div class="tool-hint" *ngIf="activeTool() !== 'select'">
                    <ng-container *ngIf="activeTool() === 'measure'">
                        {{ measureStart ? 'Click the second point to finish the dimension.' : 'Click the first point to start the dimension.' }}
                    </ng-container>
                    <ng-container *ngIf="activeTool() === 'direction'">
                        {{ directionStart ? 'Click the end point to place the direction marker.' : 'Click where the direction line starts.' }}
                    </ng-container>
                    <ng-container *ngIf="activeTool() === 'crop'">
                        Drag a box around the drawing area, then crop or rotate the selected content.
                    </ng-container>
                    <ng-container *ngIf="activeTool() !== 'measure' && activeTool() !== 'direction' && activeTool() !== 'crop'">
                        Click to add points. Double-click to finish the shape.
                    </ng-container>
                </div>
            </div>

            <div class="panel-card" *ngIf="activeTool()==='draw-custom'">
                <div class="panel-heading compact">
                    <h3>Custom Line</h3>
                </div>
                <div class="form-group">
                    <label>Colour</label>
                    <div class="color-row">
                        <input type="color" [(ngModel)]="customLineColor" class="color-picker">
                        <span class="color-hex">{{ customLineColor }}</span>
                    </div>
                </div>
                <div class="form-group">
                    <label>Style</label>
                    <select [(ngModel)]="customLineStyle" class="prop-select">
                        <option *ngFor="let s of STYLE_OPTIONS" [value]="s.value">{{ s.label }}</option>
                    </select>
                </div>
                <div class="form-group">
                    <label>Width (px)</label>
                    <input type="number" [(ngModel)]="customLineWidth" min="1" max="8" class="prop-input">
                </div>
                <div class="form-group">
                    <label>Label</label>
                    <input type="text" [(ngModel)]="customLineLabel" placeholder="e.g. 150mm SW" class="prop-input">
                </div>
            </div>

            <div class="panel-card grow">
                <div class="panel-heading compact">
                    <h3>Fixture Library</h3>
                    <p>Tap a code to place it on the drawing.</p>
                </div>

                <div class="fixture-section">
                    <span class="fixture-title">Sanitary</span>
                    <div class="fixtures-grid dense">
                        <button *ngFor="let f of SEWER_FIXTURES" (click)="addFixture(f)" class="lib-btn ss-btn">{{ f }}</button>
                    </div>
                </div>

                <div class="fixture-section">
                    <span class="fixture-title">Stormwater</span>
                    <div class="fixtures-grid dense">
                        <button *ngFor="let f of SW_FIXTURES" (click)="addFixture(f)" class="lib-btn sw-btn">{{ f }}</button>
                    </div>
                </div>

                <div class="fixture-section">
                    <span class="fixture-title">General</span>
                    <div class="fixtures-grid dense">
                        <button *ngFor="let f of GENERAL_FIXTURES" (click)="addFixture(f)" class="lib-btn gen-btn">{{ f }}</button>
                    </div>
                </div>
            </div>
        </aside>

        <main class="canvas-stage">
            <div class="canvas-meta">
                <div class="meta-block">
                    <span>Scale</span>
                    <strong>1 px = {{ metresPerPixel() | number:'1.3-3' }} m</strong>
                </div>
                <div class="meta-block">
                    <span>Pipe Length</span>
                    <strong>{{ totalPipeLengthMetres() | number:'1.1-1' }} m</strong>
                </div>
                <div class="meta-block">
                    <span>Building Area</span>
                    <strong>{{ buildingAreaSqm() | number:'1.1-1' }} m²</strong>
                </div>
                <div class="meta-block">
                    <span>Drawing</span>
                    <strong>{{ CANVAS_W_ }} × {{ CANVAS_H_ }}</strong>
                </div>
            </div>

            <div class="canvas-workspace">
                <div class="canvas-wrapper">
                    <ko-stage [config]="configStage()" (click)="onStageClick($event)" (dblclick)="onStageDblClick($event)"
                        (mousemove)="onStageMouseMove($event)">
                        <ko-layer>
                            @if (snapToGrid()) {
                            @for (row of gridRows; track row) {
                            <ko-line [config]="{ points:[0,row*gridSize,CANVAS_W_,row*gridSize], stroke:'#e8ecf0', strokeWidth:0.5, listening:false }"></ko-line>
                            }
                            @for (col of gridCols; track col) {
                            <ko-line [config]="{ points:[col*gridSize,0,col*gridSize,CANVAS_H_], stroke:'#e8ecf0', strokeWidth:0.5, listening:false }"></ko-line>
                            }
                            }

                            @if (buildingOutline().length > 0) {
                            <ko-line [config]="{ points: buildingOutlinePoints(), closed:true, stroke: isBuildingSelected() ? '#f59e0b' : '#1a1a1a', strokeWidth: isBuildingSelected() ? 4 : 3, fill:'rgba(248,248,244,0.45)', lineJoin:'miter', listening: !isDrawingMode(), hitStrokeWidth: 16 }"
                                (click)="selectItem('building-outline')"></ko-line>
                            @if (isBuildingSelected()) {
                                @for (handle of boundaryPointHandles(); track handle.index) {
                                    <ko-circle [config]="getBoundaryHandleConfig(handle)" (dragend)="onBoundaryPointDragEnd($event, handle.index)"></ko-circle>
                                }
                            }
                            }

                            @for (pipe of pipes(); track pipe.id) {
                            <ko-line [config]="getPipeConfig(pipe)" (click)="selectItem(pipe.id)" (dragend)="onPipeDragEnd($event, pipe.id)"></ko-line>
                            }

                            @for (m of measurements(); track m.id) {
                            <ko-group [config]="getMeasureGroupConfig(m)" (dragend)="onMeasureDragEnd($event, m.id)"
                                (click)="selectItem(m.id)">
                                <ko-line [config]="getMeasureLineConfig(m)"></ko-line>
                                <ko-line [config]="getTickConfig(m.x1, m.y1, m.axis)"></ko-line>
                                <ko-line [config]="getTickConfig(m.x2, m.y2, m.axis)"></ko-line>
                                <ko-rect [config]="getMeasureLabelBgConfig(m)"></ko-rect>
                                <ko-text [config]="getMeasureLabelConfig(m)"></ko-text>
                            </ko-group>
                            }

                            @for (line of directionalLines(); track line.id) {
                            <ko-group [config]="getDirectionGroupConfig(line)" (dragend)="onDirectionDragEnd($event, line.id)"
                                (click)="selectItem(line.id)">
                                <ko-line [config]="getDirectionLineConfig(line)"></ko-line>
                                @for (arrow of directionArrowheads(line); track arrow[0] + '-' + arrow[1]) {
                                    <ko-line [config]="getDirectionArrowConfig(arrow)"></ko-line>
                                }
                                <ko-circle [config]="getDirectionDotConfig(line)"></ko-circle>
                                @if (line.label) {
                                    <ko-rect [config]="getDirectionLabelBgConfig(line)"></ko-rect>
                                    <ko-text [config]="getDirectionLabelConfig(line)"></ko-text>
                                }
                            </ko-group>
                            }

                            @for (fix of fixtures(); track fix.id) {
                            <ko-group [config]="getFixtureGroupConfig(fix)" (dragend)="onFixtureDragEnd($event, fix.id)"
                                (click)="selectItem(fix.id)">
                                @if (selectedId() === fix.id) {
                                <ko-circle [config]="{ x:fix.x, y:fix.y, radius:20, fill:'rgba(245,165,0,0.18)', stroke:'#f5a500', strokeWidth:1.5, listening:false }"></ko-circle>
                                }
                                <ko-circle [config]="getFixtureCircleConfig(fix)"></ko-circle>
                                <ko-text [config]="getFixtureTextConfig(fix)"></ko-text>
                            </ko-group>
                            }

                            @if (previewLine().length >= 4) {
                            <ko-line [config]="getPreviewLineConfig()"></ko-line>
                            }

                            @if (cropRect()) {
                            <ko-rect [config]="getCropRectConfig()"></ko-rect>
                            }

                            @if (activeTool() === 'measure' && measureStart) {
                            <ko-circle [config]="getMeasureStartConfig()"></ko-circle>
                            }
                            @if (activeTool() === 'direction' && directionStart) {
                            <ko-circle [config]="getDirectionStartConfig()"></ko-circle>
                            }
                        </ko-layer>
                    </ko-stage>

                    @if (buildingOutline().length === 0 && pipes().length === 0 && fixtures().length === 0 && directionalLines().length === 0) {
                    <div class="empty-hint">
                        <div class="empty-icon">□</div>
                        <p>Start drafting the plan</p>
                        <span>Use the left tool rail to draw the outline, add lines, place fixtures, and set dimensions.</span>
                    </div>
                    }

                    @if (isDrawingMode()) {
                    <div class="draw-overlay"
                         (mousedown)="onCanvasMouseDown($event)"
                         (mouseup)="onCanvasMouseUp($event)"
                         (click)="onCanvasClick($event)"
                         (dblclick)="onCanvasDblClick($event)"
                         (mousemove)="onCanvasMouseMove($event)">
                    </div>
                    }
                </div>
            </div>
        </main>

        <aside class="inspector-panel">
            <div class="panel-card crop-workflow">
                <div class="panel-heading">
                    <h3>Crop Workflow</h3>
                    <p>Use this when the PDF has blank space or the drawing sits off-centre.</p>
                </div>
                <ol class="workflow-list">
                    <li><span>1</span><p>Choose <strong>Manual Crop</strong> from the left tools.</p></li>
                    <li><span>2</span><p>Drag around the exact drawing area you want to keep.</p></li>
                    <li><span>3</span><p>Apply the crop or rotate the selected drawing, then review scale and generate PDF.</p></li>
                </ol>
            </div>

            <div class="panel-card">
                <div class="panel-heading">
                    <h3>Scale & Crop</h3>
                    <p>Control drawing size and sheet fit.</p>
                </div>
                <div class="form-group">
                    <label>Drawing width in metres</label>
                    <input type="number" min="1" step="0.1" [ngModel]="drawingWidthMetres()"
                        (ngModelChange)="setDrawingWidthMetres($event)" placeholder="30">
                </div>
                <div class="scale-actions">
                    <button type="button" class="scale-btn" (click)="saveScaleAsDefault()">Save global default</button>
                    <button type="button" class="scale-btn" (click)="recalculateMeasurements()" [disabled]="measurements().length === 0">Recalculate labels</button>
                    <button type="button" class="scale-btn" (click)="fitDrawingToCanvas()" [disabled]="!hasDrawingContent()">Fit / crop blank space</button>
                    <button type="button" class="scale-btn" (click)="applyManualCrop()" [disabled]="!cropRect()">Apply manual crop</button>
                    <div class="rotate-actions">
                        <button type="button" class="scale-btn" (click)="rotateSelectedDrawing(-90)" [disabled]="!cropRect()">Rotate 90&deg; left</button>
                        <button type="button" class="scale-btn" (click)="rotateSelectedDrawing(90)" [disabled]="!cropRect()">Rotate 90&deg; right</button>
                        <button type="button" class="scale-btn" (click)="rotateSelectedDrawing(180)" [disabled]="!cropRect()">Rotate 180&deg;</button>
                    </div>
                    <button type="button" class="scale-btn subtle" (click)="clearManualCrop()" [disabled]="!cropRect()">Clear crop box</button>
                </div>
            </div>

            @if (selectedPipe()) {
            <div class="panel-card">
                <div class="panel-heading">
                    <h3>Selected Line</h3>
                    <p>Adjust the real length of the selected pipe.</p>
                </div>
                <div class="selected-line-card">
                    <div>
                        <span>Current length</span>
                        <strong>{{ selectedLineLengthMetres() | number:'1.1-1' }} m</strong>
                    </div>
                    <div class="form-group">
                        <label>Set length in metres</label>
                        <input type="number" min="0.1" step="0.1" [ngModel]="selectedLineLengthMetres()"
                            (change)="setSelectedLineLength($any($event.target).value)">
                    </div>
                </div>
            </div>
            }

            @if (selectedMeasurement()) {
            <div class="panel-card">
                <div class="panel-heading">
                    <h3>Selected Measurement</h3>
                    <p>Edit text only. The measurement endpoints stay exactly where they are.</p>
                </div>
                <div class="form-group">
                    <label>Manual text</label>
                    <input type="text" [ngModel]="selectedMeasurement()?.label"
                        (ngModelChange)="updateMeasureLabel(selectedMeasurement()!.id, $event)"
                        placeholder="e.g. 4.2M">
                </div>
            </div>
            }

            @if (selectedDirectionLine()) {
            <div class="panel-card">
                <div class="panel-heading">
                    <h3>Direction Line</h3>
                    <p>Use this for flow or direction marks like the red arrow reference.</p>
                </div>
                <div class="form-group">
                    <label>Optional label</label>
                    <input type="text" [ngModel]="selectedDirectionLine()?.label || ''"
                        (ngModelChange)="updateDirectionLabel(selectedDirectionLine()!.id, $event)"
                        placeholder="e.g. fall direction">
                </div>
            </div>
            }

            @if (isBuildingSelected()) {
            <div class="panel-card">
                <div class="panel-heading">
                    <h3>Boundary</h3>
                    <p>Drag the orange corner handles to fix the outline without starting again.</p>
                </div>
                <button type="button" class="scale-btn subtle" (click)="setTool('draw-building')">Redraw boundary only</button>
            </div>
            }

            <div class="panel-card template-card">
                <div class="panel-heading">
                    <h3>Template Details</h3>
                    <p>These fields flow into the AC1203 PDF.</p>
                </div>
                <div class="inspector-form">
                    <div class="form-group"><label>Consent #</label><input [(ngModel)]="buildingConsent" placeholder="BCO10393431"></div>
                    <div class="form-group"><label>Owner</label><input [(ngModel)]="ownerName" placeholder="Full Name"></div>
                    <div class="form-group"><label>Address</label><input [(ngModel)]="jobAddress" placeholder="25 Muster Rd, Pukekohe"></div>
                    <div class="form-grid two">
                        <div class="form-group"><label>Postcode</label><input [(ngModel)]="postcode" placeholder="2113"></div>
                        <div class="form-group"><label>Lot number</label><input [(ngModel)]="lotNumber" placeholder="37"></div>
                    </div>
                    <div class="form-group"><label>Legal Description</label><input [(ngModel)]="legalDescription" placeholder="Lot 36 DP 594131"></div>
                    <div class="form-grid two">
                        <div class="form-group"><label>DP number</label><input [(ngModel)]="dpNumber" placeholder="594131"></div>
                        <div class="form-group"><label>Registration No.</label><input [(ngModel)]="regNumber" placeholder="29212"></div>
                    </div>
                    <div class="form-group"><label>Drain-layer</label><input [(ngModel)]="drainLayer" placeholder="Name"></div>
                    <div class="form-group"><label>Date</label><input [(ngModel)]="dateSubmitted" placeholder="11-July-2025"></div>
                    <div class="form-group">
                        <label>Notes</label>
                        <textarea [(ngModel)]="notes" rows="3" placeholder="Access notes, drawing clarifications, council comments, or field observations"></textarea>
                    </div>
                </div>
            </div>

            <div class="panel-card" *ngIf="measurements().length > 0">
                <div class="panel-heading compact">
                    <h3>Manual Measurement Text</h3>
                    <p>Edit the printed dimension text when AI or scale calculation is wrong.</p>
                </div>
                <div class="measurement-list">
                    <div *ngFor="let m of measurements()" class="msr-row">
                        <div class="msr-dot" [class.sel]="selectedId()===m.id" (click)="selectItem(m.id)">↔</div>
                        <label class="msr-field">
                            <span>Manual text</span>
                            <input class="msr-input" [value]="m.label" placeholder="e.g. 4.2M"
                                (change)="updateMeasureLabel(m.id, $any($event.target).value)">
                        </label>
                    </div>
                </div>
            </div>

            <div class="panel-card">
                <div class="panel-heading compact">
                    <h3>Sheet Settings</h3>
                </div>
                <label class="toggle">
                    <input type="checkbox" [checked]="snapToGrid()" (change)="snapToGrid.set(!snapToGrid())">
                    Snap to Grid ({{ gridSize }}px)
                </label>
                @if (sourceImageHref()) {
                    <a class="source-link" [href]="sourceImageHref()" target="_blank" rel="noopener">View source image</a>
                }

                <div class="stats-grid compact">
                    <div class="stat-item ss"><span class="stat-val">{{ ssNodeCount() }}</span><span class="stat-lbl">SS Nodes</span></div>
                    <div class="stat-item sw"><span class="stat-val">{{ swNodeCount() }}</span><span class="stat-lbl">SW Nodes</span></div>
                    <div class="stat-item ss"><span class="stat-val">{{ ssSegCount() }}</span><span class="stat-lbl">SS Segs</span></div>
                    <div class="stat-item sw"><span class="stat-val">{{ swSegCount() }}</span><span class="stat-lbl">SW Segs</span></div>
                    <div class="stat-item gen"><span class="stat-val">{{ genNodeCount() }}</span><span class="stat-lbl">General</span></div>
                    <div class="stat-item gen"><span class="stat-val">{{ measurements().length }}</span><span class="stat-lbl">Dims</span></div>
                    <div class="stat-item gen"><span class="stat-val">{{ directionalLines().length }}</span><span class="stat-lbl">Dirs</span></div>
                </div>
            </div>

        </aside>
    </div>
</div>
